import { matchPath } from "react-router-dom";
import { AppRoutes, DashboardModules, MODULE_TO_SIDEBAR_LINKS } from "../../config/app.config";
import { ALLOW_ALL } from "../../config/role.config";

/**
 * @param {string} pathname - `location.pathname` (no query string)
 * @param {string[]|'*'} patterns - `matchPath` patterns with `end: true`
 */
export function pathnameMatchesAllowedPatterns(pathname, patterns) {
	if (!Array.isArray(patterns)) {
		return false;
	}
	return patterns.some((pattern) => matchPath(pattern, pathname));
}

/**
 * Whether a sidebar sublink is allowed, using the same check as before:
 * Kiosk sublinks use `route.match`; Studio sublinks use `link` as the path to test.
 */
function subLinkMatchesAllowedPages(subLink, allowedPages) {
	const candidate = subLink.route?.match ?? subLink.link;
	if (candidate == null) {
		return false;
	}
	return pathnameMatchesAllowedPatterns(candidate, allowedPages);
}

export function normalizeAllowedModules(allowedModules) {
	if (allowedModules === ALLOW_ALL) {
		return Object.keys(DashboardModules);
	}
	return Array.isArray(allowedModules) ? [...allowedModules] : [];
}

/**
 * Collect `matchPath` patterns registered for a dashboard module.
 * - HOME: flat — `{ INDEX: { match, ... } }`
 * - KIOSK / STUDIO: nested — `{ SUBMODULE: { ROUTE_KEY: { match, ... } } }`
 */
export function getAllPagesForModule(module) {
	const moduleRoutesConfig = AppRoutes[module];
	if (!moduleRoutesConfig) return [];
	const pages = [];

	for (const key in moduleRoutesConfig) {
		const value = moduleRoutesConfig[key];
		if (!value || typeof value !== "object") continue;

		if (typeof value.match === "string") {
			pages.push(value.match);
			continue;
		}

		for (const routeKey in value) {
			const route = value[routeKey];
			if (route && typeof route.match === "string") {
				pages.push(route.match);
			}
		}
	}
	return pages;
}

export function resolvePatternsForModule(rolePermissions, appModule) {
	const pages = rolePermissions.allowedPages;
	if (pages === ALLOW_ALL) {
		return "*";
	}
	if (!pages || typeof pages !== "object") {
		return [];
	}
	const modulePages = pages[appModule];
	if (modulePages === ALLOW_ALL) {
		return getAllPagesForModule(appModule);
	}
	if (Array.isArray(modulePages)) {
		return modulePages;
	}
	return [];
}

/**
 * Derive effective `matchPath` pattern lists per module from role config (once per role).
 */
export function deriveAccess(rolePermissions, role) {
	const accessConfig = {
		allowedModules: [],
		allowedPages: [],
		allowedSidebarLinksPerModule: {},
	};

	if (!role) {
		return accessConfig;
	}

	const entry = rolePermissions[role];
	if (!entry) {
		return accessConfig;
	}

	const modules = normalizeAllowedModules(entry.allowedModules);
	const canAccessAllPages = typeof entry.allowedPages === 'string' && entry.allowedPages === ALLOW_ALL;

	for(const module of modules) {
		const pages = getAllPagesForModule(module);
		if(canAccessAllPages) {
			accessConfig.allowedPages.push(...pages);
		} else {
			const allowedPages = entry.allowedPages[module];
			if(!allowedPages) continue;
			for (const page of pages) {
				if (pathnameMatchesAllowedPatterns(page, allowedPages)) {
					accessConfig.allowedPages.push(page);
				}
			}
		}
	}

	for (const appModule of modules) {
		const sidebarLinks = MODULE_TO_SIDEBAR_LINKS[appModule];
		if (!sidebarLinks) {
			continue;
		}
		/** Same shape as `MODULE_TO_SIDEBAR_LINKS` (e.g. `KioskSidebarLinks`), not a flat array — `Sidebar` expects section objects with `hasSubLinks` / `subLinks`. */
		const filteredSidebar = {};
		for (const sectionKey of Object.keys(sidebarLinks)) {
			const section = sidebarLinks[sectionKey];
			const subLinks = section.subLinks || [];
			// Use role-level flag — `accessConfig.allowedPages` is always an array after flattening, never `ALLOW_ALL`.
			const canAccessAllSubLinks = canAccessAllPages;
			if (canAccessAllSubLinks) {
				filteredSidebar[sectionKey] = { ...section, subLinks: subLinks };
			} else {
				const kept = subLinks.filter((subLink) =>
					subLinkMatchesAllowedPages(subLink, accessConfig.allowedPages)
				);
				if (kept.length > 0) {
					filteredSidebar[sectionKey] = { ...section, subLinks: kept };
				}
			}
		}
		accessConfig.allowedSidebarLinksPerModule[appModule] = filteredSidebar;
	}

	return {
		allowedModules: modules,
		allowedPages: accessConfig.allowedPages,
		allowedSidebarLinksPerModule: accessConfig.allowedSidebarLinksPerModule,
	};
}